import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, Box,
    FormControlLabel, Switch
} from '@mui/material';
import { Save as SaveIcon, Close as CloseIcon } from '@mui/icons-material';

import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';

const emptyForm = { name: '', description: '', isActive: true };

const TeamDialog = () => {
    const dispatch = useDispatch();
    const isOpen = useSelector(selectors.isTeamDialogOpenSelector);
    const selected = useSelector(selectors.selectedTeamSelector);
    const loading = useSelector(selectors.loadingSelector);

    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const isEdit = !!selected;

    useEffect(() => {
        if (isOpen && selected) {
            setForm({
                name: selected.name || '',
                description: selected.description || '',
                isActive: selected.isActive !== undefined ? selected.isActive : true
            });
        } else if (isOpen) {
            setForm(emptyForm);
        }
        setErrors({});
    }, [isOpen, selected]);

    const handleClose = () => { dispatch(actions.closeTeamDialog()); setForm(emptyForm); setErrors({}); };
    const handleChange = (field) => (e) => {
        const v = field === 'isActive' ? e.target.checked : e.target.value;
        setForm((p) => ({ ...p, [field]: v }));
        if (errors[field]) setErrors((p) => ({ ...p, [field]: null }));
    };

    const validate = () => {
        const ne = {};
        if (!form.name.trim()) ne.name = 'Name is required';
        else if (form.name.trim().length > 255) ne.name = 'Name must not exceed 255 characters';
        setErrors(ne);
        return Object.keys(ne).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;
        const payload = {
            name: form.name.trim(),
            description: form.description.trim() || undefined,
            isActive: form.isActive
        };
        Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
        if (isEdit) dispatch(actions.updateTeam(selected.id, payload));
        else dispatch(actions.createTeam(payload));
    };

    return (
        <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEdit ? 'Edit Team' : 'Create New Team'}</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ pt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField label="Name" fullWidth required size="small"
                                value={form.name} onChange={handleChange('name')}
                                error={!!errors.name} helperText={errors.name}
                                inputProps={{ maxLength: 255 }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Description" fullWidth multiline rows={3} size="small"
                                value={form.description} onChange={handleChange('description')} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={form.isActive} onChange={handleChange('isActive')} />}
                                label={form.isActive ? 'Active' : 'Inactive'}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button onClick={handleClose} startIcon={<CloseIcon />} disabled={loading}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" startIcon={<SaveIcon />} disabled={loading}>
                    {isEdit ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TeamDialog;
